import { sendErrorMetric } from "@expert/cloudwatch-metrics";
import { useNotification } from "@expert/common-ui";
import { type JSX, useEffect, useState } from "react";

import type { CommunicationType } from "../../../../../types";

import { useSalesContext } from "../../../../../sales-context/SalesContext";
import { formatPhoneNumber } from "../../../../../shared-utils";
import { type OfferChecklistState, useOfferChecklistStore } from "../../../../../stores/useOfferChecklistStore";
import { useVerifiedCustomerStore } from "../../../../../stores/useVerifiedCustomerStore";
import { GuidedBrowsingModal, useGuidedBrowsingStore } from "../../../../guided-browsing";
import { useHandleSendLink } from "../../SendLink/HandleSendLink";
import { GuidedBrowsingContentView } from "./GuidedBrowsingContentView";

export function GuidedBrowsingContent({
    sendLinkCopy,
    sentSuccessMessage,
    checklistPlacement = "TRANSITION",
    requiresChecklistCompletion = true,
    returnLinkToExpert = true,
    sendLinkCommType,
}: {
    sendLinkCopy?: string;
    sentSuccessMessage?: string;
    checklistPlacement?: string;
    requiresChecklistCompletion?: boolean;
    returnLinkToExpert?: boolean;
    sendLinkCommType?: string;
}): JSX.Element {
    const { sessionId, partner, logger, analytics, isSimulatedVoiceTask } = useSalesContext();
    const notification = useNotification();
    const { customer } = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId));
    const guidedBrowsingSession = useGuidedBrowsingStore((state) => state.getSession(sessionId));
    // Actions are stable refs; read once without subscribing.
    const { receiveData, openModal } = useGuidedBrowsingStore.getState();
    const data = useOfferChecklistStore((state) => state.getData(sessionId));
    const sendLink = useHandleSendLink({ isGuidedBrowsing: true });

    const initialPhoneNumber = formatPhoneNumber(customer?.mdn ?? "", undefined, true).number || customer?.mdn || "";
    const [commType, setCommType] = useState<CommunicationType>((sendLinkCommType as CommunicationType) ?? "SMS");
    const [contact, setContact] = useState(commType === "EMAIL" ? (customer?.email ?? "") : initialPhoneNumber);
    const [loading, setLoading] = useState(false);

    const isEmailType = commType === "EMAIL";
    const contactForApi = isEmailType ? contact : formatInputMDN(contact);
    const deliveryMethod = isEmailType ? "email" : "sms";

    const canShowGuidedBrowsingButton =
        !requiresChecklistCompletion || areGuidedBrowsingPrerequisitesComplete(data, checklistPlacement);
    const guidedLinkSent = guidedBrowsingSession?.deliveryStatus === "sent";
    const isSendDisabled = (!canShowGuidedBrowsingButton && !guidedLinkSent) || !contact || isSimulatedVoiceTask;
    const showOverrideAction = !canShowGuidedBrowsingButton && !guidedLinkSent;
    const showViewSentLinkAction = returnLinkToExpert && guidedLinkSent && !guidedBrowsingSession?.isModalOpen;

    const handleContactChange = (inputValue: string) => {
        if (isEmailType) {
            setContact(inputValue);
        } else {
            setContact(formatInputMDNDisplay(inputValue));
        }
    };

    const handleCommTypeChange = (value: string) => {
        setCommType(value);
        if (value === "EMAIL") {
            setContact(customer?.email ?? "");
        } else {
            setContact(initialPhoneNumber);
        }
    };

    const handleSend = async (isOverride: boolean) => {
        setLoading(true);
        try {
            logger.info(
                { isOverride },
                isOverride
                    ? "Override: Attempting to send guided browsing link without checklist prerequisites"
                    : "Attempting to send guided browsing link",
            );
            if (isOverride) {
                void analytics.dispatcher.dispatchBusinessEvent("GuidedBrowsingOverrideUsed", {
                    sessionId,
                    partner,
                    deliveryMethod,
                });
            }
            const response = await sendLink(commType, contactForApi);

            notification.success(sentSuccessMessage || DEFAULT_SENT_SUCCESS_MESSAGE);
            logger.info({ url: response.url, isOverride, returnLinkToExpert }, "Guided browsing link sent");
            if (!response.url && returnLinkToExpert) {
                logger.warn({ response, isOverride }, "Guided browsing link sent but response missing url");
            }
            receiveData(sessionId, {
                url: response.url ?? "",
                deliveryStatus: "sent",
                deliveryMethod,
            });
            if (!returnLinkToExpert) {
                useGuidedBrowsingStore.getState().closeModal(sessionId);
            }
        } catch (error) {
            notification.error("Failed to send guided browsing link. Please try again.");
            logger.error({ error, isOverride }, "Error sending guided browsing link");
            void sendErrorMetric("GuidedBrowsingLinkError", { partner });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (commType === "EMAIL" && customer?.email) {
            setContact(customer.email);
        } else if (commType === "SMS" && customer?.mdn) {
            const formatted = formatPhoneNumber(customer.mdn, undefined, true).number || customer.mdn;
            setContact(formatted);
        }
    }, [customer?.mdn, customer?.email, commType]);

    return (
        <>
            <GuidedBrowsingContentView
                commType={commType}
                contact={contact}
                isEmailType={isEmailType}
                loading={loading}
                isSendDisabled={isSendDisabled}
                isOverrideDisabled={loading || !contact || isSimulatedVoiceTask}
                showOverrideAction={showOverrideAction}
                showViewSentLinkAction={showViewSentLinkAction}
                sendLinkCopy={sendLinkCopy}
                showCommTypeSelector={!sendLinkCommType}
                onCommTypeChange={handleCommTypeChange}
                onContactChange={handleContactChange}
                onSend={() => {
                    void handleSend(false);
                }}
                onSendOverride={() => {
                    void handleSend(true);
                }}
                onViewSentLink={() => openModal(sessionId)}
            />
            <GuidedBrowsingModal sessionId={sessionId} logger={logger} />
        </>
    );
}

const DEFAULT_SENT_SUCCESS_MESSAGE = "Guided browsing link sent successfully!";

const sanitizePhoneNumber = (phone: string | undefined): string => {
    if (!phone) return "";
    return phone.replace(/\D/g, "");
};

const formatInputMDN = (inputMdn: string | undefined): string => {
    if (!inputMdn) return "";
    return `+1${sanitizePhoneNumber(inputMdn)}`;
};

const formatInputMDNDisplay = (inputMdn: string): string => {
    const sanitized = sanitizePhoneNumber(inputMdn);
    if (sanitized.length <= 3) return `(${sanitized}`;
    if (sanitized.length <= 6) return `(${sanitized.slice(0, 3)}) ${sanitized.slice(3)}`;
    return `(${sanitized.slice(0, 3)}) ${sanitized.slice(3, 6)}-${sanitized.slice(6, 10)}`;
};

/**
 * Check if the required prerequisite checklist items are completed
 * before allowing the guided browsing button to be shown.
 */
const areGuidedBrowsingPrerequisitesComplete = (
    data: OfferChecklistState["data"],
    checklistPlacement = "TRANSITION",
): boolean => {
    return (data as Record<string, boolean>)[checklistPlacement] ?? false;
};
